import { useState, useEffect } from 'react';
import { useSession } from './useSession.ts';
import { supabase } from '../services/supabaseClient.ts';

export interface UserScan {
  id: string;
  user_id: string;
  source_type: string;
  status: string;
  created_at: string;
  findings_count?: number;
}

export function useUserScans() {
  const { session, loading: sessionLoading } = useSession();
  const [scans, setScans] = useState<UserScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUserScans() {
      if (sessionLoading) return;
      
      if (!session?.user?.id) {
        setScans([]);
        setLoading(false);
        return;
      }

      try {
        console.log('[useUserScans] Fetching scans for user:', session.user.id);
        setLoading(true);
        setError(null);

        // Fetch scans with findings count
        const { data: scansData, error: scansError } = await supabase
          .from('scans')
          .select(`
            id,
            user_id,
            source_type,
            status,
            created_at
          `)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (scansError) {
          throw scansError;
        }

        // Get findings count for each scan
        const scansWithCounts = await Promise.all(
          (scansData || []).map(async (scan) => {
            const { count } = await supabase
              .from('scan_results')
              .select('*', { count: 'exact', head: true })
              .eq('scan_id', scan.id);

            return {
              ...scan,
              findings_count: count || 0
            };
          })
        );

        console.log('[useUserScans] Found', scansWithCounts.length, 'scans');
        setScans(scansWithCounts);
      } catch (err) {
        console.error('[useUserScans] Error fetching scans:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch scans');
        setScans([]);
      } finally {
        setLoading(false);
      }
    }

    fetchUserScans();
  }, [session?.user?.id, sessionLoading]);

  const refetch = async () => {
    if (sessionLoading) return;
    
    if (!session?.user?.id) {
      setScans([]);
      setLoading(false);
      return;
    }

    try {
      console.log('[useUserScans] Refetching scans for user:', session.user.id);
      setLoading(true);
      setError(null);

      // Fetch scans with findings count
      const { data: scansData, error: scansError } = await supabase
        .from('scans')
        .select(`
          id,
          user_id,
          source_type,
          status,
          created_at
        `)
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (scansError) {
        throw scansError;
      }

      // Get findings count for each scan
      const scansWithCounts = await Promise.all(
        (scansData || []).map(async (scan) => {
          const { count } = await supabase
            .from('scan_results')
            .select('*', { count: 'exact', head: true })
            .eq('scan_id', scan.id);

          return {
            ...scan,
            findings_count: count || 0
          };
        })
      );

      console.log('[useUserScans] Refetched', scansWithCounts.length, 'scans');
      setScans(scansWithCounts);
    } catch (err) {
      console.error('[useUserScans] Error refetching scans:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch scans');
      setScans([]);
    } finally {
      setLoading(false);
    }
  };

  return { scans, loading, error, refetch };
} 